/**
 * GAME 4 — DUO (binairo/takuzu). Unicité par creusage vérifié.
 * Durci vs le prototype (analyse Monte-Carlo) :
 * - profondeur réelle par niveau : le palier `basic` de duPropagate (règles locales
 *   seules) ne doit PAS suffire dès le niveau 1 (≥ 6 cases irrésolues au niveau 2) ;
 * - défis « jumeaux » (di 1/4/5, niv ≥ 1) : la règle jumeaux est réellement nécessaire ;
 * - Éclipse : vies dérivées de la taille (1/2/3 en 6/10/14) au lieu d'une vie sèche ;
 * - 14×14 : +6 pts d'indices (moins de corvée) ; `par` posé pour le score.
 */
import type { GameEngine, PuzzleBase, Rng } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { deep, mk } from './util';

export interface DuoPuzzle extends PuzzleBase {
  g: 'duo';
  N: number;
  di: number;
  uniq: boolean;
  sol: number[][];
  giv: number[][];
  cur: number[][];
  lives: number | null;
  livesMax: number | null;
  touch?: [number, number] | null;
  flash?: [number, number] | null;
}

export function duValid(g: number[][], N: number, final: boolean): boolean {
  const half = N / 2;
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) {
      const v = g[r][c];
      if (v === -1) continue;
      if (c >= 2 && g[r][c - 1] === v && g[r][c - 2] === v) return false;
      if (r >= 2 && g[r - 1][c] === v && g[r - 2][c] === v) return false;
    }
  for (let r = 0; r < N; r++) {
    let a = 0,
      b = 0;
    for (let c = 0; c < N; c++) {
      if (g[r][c] === 0) a++;
      else if (g[r][c] === 1) b++;
    }
    if (a > half || b > half) return false;
    if (final && (a !== half || b !== half)) return false;
  }
  for (let c = 0; c < N; c++) {
    let a = 0,
      b = 0;
    for (let r = 0; r < N; r++) {
      if (g[r][c] === 0) a++;
      else if (g[r][c] === 1) b++;
    }
    if (a > half || b > half) return false;
    if (final && (a !== half || b !== half)) return false;
  }
  return true;
}

export function duGenFull(N: number, uniq: boolean, rng: Rng): number[][] | null {
  // Las Vegas : propagation + assignation aléatoire, redémarrage sur contradiction
  outer: for (let restart = 0; restart < 500; restart++) {
    let w = mk(N, -1);
    for (let guard = 0; guard < N * N + 8; guard++) {
      w = duPropagate(w, N, false);
      if (!duValid(w, N, false)) continue outer;
      const unk: [number, number][] = [];
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (w[r][c] === -1) unk.push([r, c]);
      if (!unk.length) {
        if (!duValid(w, N, true)) continue outer;
        if (uniq) {
          const rows = new Set<string>(),
            cols = new Set<string>();
          for (let r = 0; r < N; r++) {
            const k = w[r].join('');
            if (rows.has(k)) continue outer;
            rows.add(k);
          }
          for (let c = 0; c < N; c++) {
            let k = '';
            for (let r = 0; r < N; r++) k += w[r][c];
            if (cols.has(k)) continue outer;
            cols.add(k);
          }
        }
        return w;
      }
      const cell = unk[ri(rng, unk.length)];
      w[cell[0]][cell[1]] = ri(rng, 2);
    }
  }
  return null;
}

interface DuLine {
  get: (i: number) => number;
  set: (i: number, v: number) => void;
  isRow: boolean;
  idx: number;
}
/** Palier de propagation : `basic` = règles locales seules (paires/encadrement/sandwich). */
export type DuTier = 'basic' | 'full';

export function duPropagate(giv: number[][], N: number, uniq: boolean, tier: DuTier = 'full'): number[][] {
  const w = giv.map((r) => r.slice());
  const half = N / 2;
  let guard = 0;
  for (;;) {
    if (++guard > 400) break;
    let ch = false;
    const lines: DuLine[] = [];
    for (let r = 0; r < N; r++)
      lines.push({
        get: (i) => w[r][i],
        set: (i, v) => {
          w[r][i] = v;
        },
        isRow: true,
        idx: r,
      });
    for (let c = 0; c < N; c++)
      lines.push({
        get: (i) => w[i][c],
        set: (i, v) => {
          w[i][c] = v;
        },
        isRow: false,
        idx: c,
      });
    for (const L of lines) {
      const a: number[] = [];
      for (let i = 0; i < N; i++) a.push(L.get(i));
      for (let i = 0; i < N; i++) {
        if (a[i] !== -1) continue;
        if (i >= 2 && a[i - 1] !== -1 && a[i - 1] === a[i - 2]) {
          L.set(i, 1 - a[i - 1]);
          a[i] = 1 - a[i - 1];
          ch = true;
          continue;
        }
        if (i + 2 < N && a[i + 1] !== -1 && a[i + 1] === a[i + 2]) {
          L.set(i, 1 - a[i + 1]);
          a[i] = 1 - a[i + 1];
          ch = true;
          continue;
        }
        if (i >= 1 && i + 1 < N && a[i - 1] !== -1 && a[i - 1] === a[i + 1]) {
          L.set(i, 1 - a[i - 1]);
          a[i] = 1 - a[i - 1];
          ch = true;
          continue;
        }
      }
      if (tier === 'basic') continue; // pas de comptage ligne/colonne en basic
      const c0 = a.filter((v) => v === 0).length,
        c1 = a.filter((v) => v === 1).length;
      if (c0 === half && c1 < half) {
        for (let i = 0; i < N; i++)
          if (a[i] === -1) {
            L.set(i, 1);
            a[i] = 1;
            ch = true;
          }
      } else if (c1 === half && c0 < half) {
        for (let i = 0; i < N; i++)
          if (a[i] === -1) {
            L.set(i, 0);
            a[i] = 0;
            ch = true;
          }
      }
    }
    if (uniq && tier === 'full') {
      for (let r = 0; r < N; r++) {
        const unk: number[] = [];
        for (let c = 0; c < N; c++) if (w[r][c] === -1) unk.push(c);
        if (unk.length === 2) {
          for (let r2 = 0; r2 < N; r2++) {
            if (r2 === r) continue;
            let complete = true;
            for (let c = 0; c < N; c++)
              if (w[r2][c] === -1) {
                complete = false;
                break;
              }
            if (!complete) continue;
            let match = true;
            for (let c = 0; c < N; c++)
              if (!unk.includes(c) && w[r][c] !== w[r2][c]) {
                match = false;
                break;
              }
            if (match) {
              w[r][unk[0]] = 1 - w[r2][unk[0]];
              w[r][unk[1]] = 1 - w[r2][unk[1]];
              ch = true;
            }
          }
        }
      }
    }
    if (!ch) break;
  }
  return w;
}

export const DUO: GameEngine<DuoPuzzle> = {
  id: 'duo',
  name: 'Duo',
  icon: '🌗',
  accent: '#ff9e6d',
  tag: "Soleil et lune, l'équilibre parfait.",
  how: 'Remplis la grille de <b>soleils et lunes</b> : jamais <b>3 identiques</b> côte à côte, et <b>autant de chaque</b> par ligne et colonne. <b>Touche</b> : soleil → lune → vide. <b>Défis à vies</b> : ta dernière case se valide quand tu touches ailleurs. Une seule solution existe.',
  sizes: [6, 10, 14, 18],
  defis: [
    { n: 'Équilibre', d: "Autant de soleils que de lunes, jamais trois d'affilée.", ic: '⚖️' },
    { n: 'Sans jumeaux', d: "Deux lignes identiques ? Interdit. Ouvre l'œil.", ic: '👯' },
    { n: 'Trois vies', d: 'Trois erreurs et le ciel se couvre.', ic: '❤️' },
    { n: 'Le vide', d: 'Une grille presque nue : tout reste à déduire.', ic: '🕳️' },
    { n: 'Face-à-face', d: 'Jumeaux interdits + indices réduits. Serré.', ic: '🪞' },
    {
      n: 'Éclipse',
      d: 'Le vide + jumeaux + vies comptées : 1 en 6×6, 2 en 10×10, 3 en 14×14. Légendaire.',
      ic: '🌘',
    },
  ],
  gen(di, N, diff, rng) {
    let uniq = di === 1 || di === 4 || di === 5;
    let sol = duGenFull(N, uniq, rng);
    if (!sol) {
      // secours : grille alternée valide (sans règle jumeaux)
      uniq = false;
      sol = mk(N, 0).map((row, r) => row.map((_, c) => (c + (r % 2)) % 2));
    }
    let keepF = [0.62, 0.52, 0.42][diff];
    if (di === 3 || di === 5) keepF = [0.5, 0.42, 0.34][diff];
    if (di === 4) keepF = [0.55, 0.46, 0.38][diff];
    if (N >= 14) keepF += 0.06; // 14×14 : ~130 cases vides = corvée, pas défi
    const targetGivens = Math.floor(keepF * N * N);
    const full = sol;

    const dig = (): number[][] => {
      const giv = full.map((r) => r.slice());
      const order: number[] = [];
      for (let i = 0; i < N * N; i++) order.push(i);
      shuffle(rng, order);
      let removed = 0;
      for (const cell of order) {
        if (N * N - removed <= targetGivens) break;
        const r = Math.floor(cell / N),
          c = cell % N,
          keep = giv[r][c];
        giv[r][c] = -1;
        const solved = duPropagate(giv, N, uniq);
        let ok = true;
        for (let i = 0; i < N && ok; i++)
          for (let j = 0; j < N; j++)
            if (solved[i][j] === -1) {
              ok = false;
              break;
            }
        if (ok) removed++;
        else giv[r][c] = keep;
      }
      return giv;
    };
    const unresolved = (w: number[][]): number => {
      let k = 0;
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (w[r][c] === -1) k++;
      return k;
    };

    // Profondeur par niveau : niv ≥ 1 → le palier basic ne complète pas la grille ;
    // niv 2 → ≥ 6 cases basic-irrésolues ; défis uniq niv ≥ 1 → la règle jumeaux est
    // réellement nécessaire (la propagation full SANS jumeaux ne complète pas).
    // ≤ 12 creusages, sinon on accepte le meilleur candidat (gen ne jette JAMAIS).
    const needBasic = diff === 2 ? 6 : diff === 1 ? 1 : 0;
    const twinNeeded = uniq && diff >= 1;
    let giv = dig();
    let bestScore = -1;
    for (let att = 0; att < 12; att++) {
      const cand = att === 0 ? giv : dig();
      const basicLeft = unresolved(duPropagate(cand, N, uniq, 'basic'));
      const twinOk = !twinNeeded || unresolved(duPropagate(cand, N, false)) > 0;
      const score = Math.min(basicLeft, needBasic) + (twinOk ? 100 : 0);
      if (score > bestScore) {
        bestScore = score;
        giv = cand;
      }
      if (basicLeft >= needBasic && twinOk) break;
    }

    // par : cycle vide→soleil→lune ⇒ 1 tap par soleil, 2 par lune à poser.
    let par = 0;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) if (giv[r][c] === -1) par += sol[r][c] === 0 ? 1 : 2;

    const lives = di === 2 ? 3 : di === 5 ? (N === 6 ? 1 : N === 10 ? 2 : 3) : null;
    return {
      g: 'duo',
      pid: ri(rng, 1e9),
      N,
      di,
      uniq,
      sol,
      giv,
      cur: giv.map((r) => r.map((v) => (v === -1 ? -1 : v))),
      lives,
      livesMax: lives,
      par,
    };
  },
  tap(p, r, c) {
    if (p.lives === 0) return false;
    // mode vies : la case précédente se valide quand on touche AILLEURS (on peut donc cycler librement)
    let committed = false;
    if (p.lives != null && p.touch && (p.touch[0] !== r || p.touch[1] !== c)) {
      const tr = p.touch[0],
        tc = p.touch[1];
      p.touch = null;
      committed = true;
      if (p.cur[tr][tc] !== -1 && p.cur[tr][tc] !== p.sol[tr][tc]) {
        p.lives--;
        p.cur[tr][tc] = -1;
        p.flash = [tr, tc];
        if (p.lives === 0) return true;
      }
    }
    if (p.giv[r][c] !== -1) return committed;
    const v = p.cur[r][c];
    p.cur[r][c] = v === -1 ? 0 : v === 0 ? 1 : -1;
    if (p.lives != null) p.touch = [r, c];
    return true;
  },
  status(p) {
    const N = p.N,
      half = N / 2;
    if (p.lives === 0) return { ok: false, txt: '💔 <b>Plus de vies…</b> 🔀 Nouvelle grille' };
    let empty = 0,
      bad = false;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        if (p.cur[r][c] === -1) {
          empty++;
          continue;
        }
        const v = p.cur[r][c];
        if (c >= 2 && p.cur[r][c - 1] === v && p.cur[r][c - 2] === v) bad = true;
        if (r >= 2 && p.cur[r - 1][c] === v && p.cur[r - 2][c] === v) bad = true;
      }
    let cntBad = false;
    for (let r = 0; r < N; r++) {
      let s = 0,
        m = 0;
      for (let c = 0; c < N; c++) {
        if (p.cur[r][c] === 0) s++;
        if (p.cur[r][c] === 1) m++;
      }
      if (s > half || m > half) cntBad = true;
    }
    for (let c = 0; c < N; c++) {
      let s = 0,
        m = 0;
      for (let r = 0; r < N; r++) {
        if (p.cur[r][c] === 0) s++;
        if (p.cur[r][c] === 1) m++;
      }
      if (s > half || m > half) cntBad = true;
    }
    let dup = false;
    if (p.uniq && !empty) {
      const rows = new Set<string>();
      for (let r = 0; r < N; r++) {
        const k = p.cur[r].join('');
        if (rows.has(k)) dup = true;
        rows.add(k);
      }
      const cols = new Set<string>();
      for (let c = 0; c < N; c++) {
        let k = '';
        for (let r = 0; r < N; r++) k += p.cur[r][c];
        if (cols.has(k)) dup = true;
        cols.add(k);
      }
    }
    if (!empty && !bad && !cntBad && !dup)
      return { ok: true, txt: '🎉 <b>Équilibre parfait !</b>' };
    const bits = [`◻ ${empty} cases vides`];
    if (bad) bits.push('⚠️ 3 identiques alignés');
    if (cntBad) bits.push("⚠️ trop d'un côté");
    if (dup) bits.push('⚠️ lignes jumelles');
    if (p.lives != null) bits.push('❤️'.repeat(p.lives) + '🖤'.repeat(p.livesMax! - p.lives));
    return { ok: false, txt: bits.join(' · ') };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N;
    ctx.fillStyle = '#241a33';
    ctx.fillRect(0, 0, S, S);
    const st = this.status(p);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const x = c * cs,
          y = r * cs,
          v = p.cur[r][c],
          giv = p.giv[r][c] !== -1;
        ctx.fillStyle = (r + c) % 2 ? '#2b2040' : '#271d3a';
        ctx.fillRect(x, y, cs, cs);
        if (v !== -1) {
          const cx = x + cs / 2,
            cy = y + cs / 2,
            rr = cs * 0.32;
          if (v === 0) {
            ctx.fillStyle = st.ok ? '#ffd166' : '#f4b942';
            if (st.ok) {
              ctx.shadowColor = '#ffd166';
              ctx.shadowBlur = cs * 0.3;
            }
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          } else {
            ctx.fillStyle = st.ok ? '#b388ff' : '#8f7bd8';
            if (st.ok) {
              ctx.shadowColor = '#b388ff';
              ctx.shadowBlur = cs * 0.3;
            }
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = (r + c) % 2 ? '#2b2040' : '#271d3a';
            ctx.beginPath();
            ctx.arc(cx + rr * 0.45, cy - rr * 0.2, rr * 0.82, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          }
          if (giv) {
            ctx.strokeStyle = 'rgba(255,255,255,.35)';
            ctx.lineWidth = Math.max(1, cs * 0.05);
            ctx.beginPath();
            ctx.arc(cx, cy, rr + cs * 0.08, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      }
    ctx.strokeStyle = 'rgba(255,255,255,.07)';
    ctx.lineWidth = 1;
    for (let i = 1; i < N; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cs, 0);
      ctx.lineTo(i * cs, S);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cs);
      ctx.lineTo(S, i * cs);
      ctx.stroke();
    }
    if (p.flash) {
      const [r, c] = p.flash;
      ctx.fillStyle = 'rgba(255,84,112,.5)';
      ctx.fillRect(c * cs, r * cs, cs, cs);
      p.flash = null;
    }
  },
  solveIt(p) {
    p.cur = deep(p.sol);
  },
};
